import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { TierBadge, PocChip } from "@/components/Badges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { PhoneCall, PhoneSlash, MagnifyingGlass, Note, UsersThree, Buildings } from "@phosphor-icons/react";
import { toast } from "sonner";

const CALL_CATEGORIES = [
  { value: "lu", label: "LU call", hint: "Low-usage outreach" },
  { value: "support", label: "Support call", hint: "Doubts / firm support" },
  { value: "other", label: "Other", hint: "Anything else" },
];

/**
 * Add / Edit a Call or a Note — unified dialog used by the Activity page and by
 * Firm 360's "Log Call" button.
 *
 * Props:
 *  - `editing`        existing entry { entry_type: "call" | "note", ... }
 *  - `lockedFirm`     { subdomain, firm_name } locks the Firm field (Firm 360)
 *  - `lockedType`     "call" — forces Call mode, hides the Type chooser. Used
 *                     from Firm 360 where Notes have their own dedicated dialog.
 *  - `onSaved`        called after a successful create/edit
 *
 * Backend dispatch:
 *  - type=call ⇒ POST/PATCH /call-logs    (fields: firm, called_at, outcome,
 *                                          remark, duration_minutes)
 *  - type=note ⇒ POST /firms/{sub}/notes  /  PATCH /notes/{id}
 *                                         (fields: firm, noted_at, body)
 */
export default function CallLogDialog({
  open, editing, lockedFirm, lockedTrial, lockedType, defaultRemark, onOpenChange, onSaved,
}) {
  const [type, setType] = useState("call"); // "call" | "note"
  const [target, setTarget] = useState("firm"); // "firm" | "internal" | "trial" | "general"
  const [category, setCategory] = useState(""); // "lu" | "support" | "other" — firm/trial calls only
  const [firmOptions, setFirmOptions] = useState([]);
  const [firmSearch, setFirmSearch] = useState("");
  const [firmSubdomain, setFirmSubdomain] = useState("");
  const [trialName, setTrialName] = useState("");
  const [trialUrl, setTrialUrl] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [outcome, setOutcome] = useState("picked_up");
  const [remark, setRemark] = useState("");
  const [hours, setHours] = useState("");
  const [minutes, setMinutes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Type chooser is shown when:
  //  - creating a new entry (no `editing`) AND no explicit lockedType from caller
  //  - editing an existing entry — the user may convert call ↔ note
  // It is hidden only when the caller explicitly locks the type (e.g., Firm 360
  // 'Log Call' button which is calls-only since Firm 360 has its own NotesDialog).
  const typeLocked = !!lockedType;
  const isNote = type === "note";
  const isCall = !isNote;
  const isInternal = isCall && target === "internal";
  const isGeneral = isNote && target === "general";
  const isTrial = target === "trial";  // applies to both calls and notes
  // The Firm/Internal or Firm/General toggle is hidden when the caller already
  // pins a firm or trial (e.g., Firm 360's Log Call button — that flow can never be
  // internal, general, or trial).
  const targetLocked = !!lockedFirm || !!lockedTrial;

  useEffect(() => {
    if (!open) return;
    if (lockedFirm) return;
    // Always preload firm options when the dialog is open without a pinned firm —
    // covers (a) brand-new firm/internal-call entries, and (b) edits where the
    // user toggles target between Internal ↔ Firm and needs the picker.
    api.get("/call-logs/firm-options").then((r) => setFirmOptions(r.data.firms));
  }, [open, lockedFirm]);

  // When the user toggles Call ↔ Note via the Type chooser, reset `target` so
  // we don't end up in an invalid combo (call+general or note+internal). "firm"
  // is the common-denominator default that's valid in both modes.
  useEffect(() => {
    if (isNote && target === "internal") setTarget("firm");
    if (isCall && target === "general") setTarget("firm");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  useEffect(() => {
    if (!open) return;
    const now = new Date();
    const initialType = editing?.entry_type || lockedType || "call";
    setType(initialType);

    if (editing) {
      const occurred = new Date(editing.occurred_at || editing.called_at || editing.noted_at);
      // Reuse the same `target` state across calls and notes:
      //   call  → "firm" | "internal" | "trial"
      //   note  → "firm" | "general"  | "trial"
      const isNoteRow = editing.entry_type === "note";
      let editingTarget = "firm";
      if (editing.target_type === "trial") editingTarget = "trial";
      else if (isNoteRow && editing.target_type === "general") editingTarget = "general";
      else if (!isNoteRow && editing.target_type === "internal") editingTarget = "internal";
      const noFirm = ["internal", "general", "trial"].includes(editingTarget);
      setTarget(editingTarget);
      setCategory(editing.call_category || "");
      setFirmSubdomain(noFirm ? "" : (editing.firm_subdomain || ""));
      setFirmSearch(noFirm ? "" : (editing.firm_name || ""));
      setTrialName(editing.trial_firm_name || "");
      setTrialUrl(editing.trial_firm_url || "");
      setDate(occurred.toISOString().slice(0, 10));
      setTime(occurred.toTimeString().slice(0, 5));
      setRemark(editing.remark || editing.body || "");
      const dm = editing.duration_minutes;
      if (dm && dm > 0) {
        setHours(String(Math.floor(dm / 60)));
        setMinutes(String(dm % 60));
      } else {
        setHours("");
        setMinutes("");
      }
      setOutcome(
        editing.entry_type === "call"
          ? (editing.type_label === "no_answer" ? "no_answer" : "picked_up")
          : "picked_up"
      );
    } else if (lockedFirm) {
      setTarget("firm");
      setCategory("");
      setFirmSubdomain(lockedFirm.subdomain);
      setFirmSearch(lockedFirm.firm_name);
      setTrialName("");
      setTrialUrl("");
      setDate(now.toISOString().slice(0, 10));
      setTime(now.toTimeString().slice(0, 5));
      setOutcome("picked_up");
      setRemark(defaultRemark || "");
      setHours("");
      setMinutes("");
    } else if (lockedTrial) {
      setTarget("trial");
      setCategory("");
      setFirmSubdomain("");
      setFirmSearch("");
      setTrialName(lockedTrial.name || "");
      setTrialUrl(lockedTrial.url || "");
      setDate(now.toISOString().slice(0, 10));
      setTime(now.toTimeString().slice(0, 5));
      setOutcome("picked_up");
      setRemark(defaultRemark || "");
      setHours("");
      setMinutes("");
    } else {
      setTarget("firm");
      setCategory("");
      setFirmSubdomain("");
      setFirmSearch("");
      setTrialName("");
      setTrialUrl("");
      setDate(now.toISOString().slice(0, 10));
      setTime(now.toTimeString().slice(0, 5));
      setOutcome("picked_up");
      setRemark(defaultRemark || "");
      setHours("");
      setMinutes("");
    }
  }, [open, editing, lockedFirm, lockedTrial, lockedType, defaultRemark]);

  const filteredFirms = useMemo(() => {
    if (!firmSearch) return firmOptions.slice(0, 30);
    const q = firmSearch.toLowerCase();
    return firmOptions
      .filter((f) => f.firm_name.toLowerCase().includes(q) || f.subdomain.toLowerCase().includes(q))
      .slice(0, 30);
  }, [firmOptions, firmSearch]);

  const remarkRequired = isNote || isInternal || outcome === "picked_up";
  // Duration is now allowed for ALL notes, internal calls, trial entries, and picked-up firm calls.
  // Hidden only when outcome is "Didn't pick up" on a firm/trial call.
  const durationAllowed = isNote || isInternal || isTrial ? true : outcome === "picked_up";
  const hoursNum = hours === "" ? 0 : Math.max(0, Math.min(8, parseInt(hours, 10) || 0));
  const minutesNum = minutes === "" ? 0 : Math.max(0, Math.min(59, parseInt(minutes, 10) || 0));
  const durationTotal = durationAllowed ? hoursNum * 60 + minutesNum : 0;
  const trialValid = !isTrial || (!!trialName.trim() && !!trialUrl.trim());
  const valid =
    date && time &&
    trialValid &&
    (!remarkRequired || remark.trim().length > 0) &&
    (
      isNote
        ? (isGeneral ? true : isTrial ? true : !!firmSubdomain)
        : isInternal
          ? true  // internal calls don't need firm or outcome
          : isTrial
            ? !!outcome  // trial calls need outcome but NOT category
            : (!!firmSubdomain && !!outcome && !!category)
    );

  const submit = async () => {
    if (!valid) return;
    setSubmitting(true);
    const occurredAt = new Date(`${date}T${time}:00`).toISOString();
    const durationPayload = !durationAllowed ? 0 : (durationTotal > 0 ? durationTotal : null);

    // Detect a type-conversion edit (call→note or note→call). We can't PATCH
    // across collections, so we create the new entry first and only delete the
    // original after success — this keeps the user's data safe if step 1 fails.
    const typeChanged = editing && editing.entry_type !== type;

    try {
      if (typeChanged) {
        if (isNote) {
          // Converting a call into a note: route to the matching note endpoint.
          if (isGeneral) {
            await api.post(`/notes/general`, {
              body: remark.trim(),
              noted_at: occurredAt,
              duration_minutes: durationPayload,
            });
          } else if (isTrial) {
            await api.post(
              `/notes/trial?trial_firm_name=${encodeURIComponent(trialName.trim())}&trial_firm_url=${encodeURIComponent(trialUrl.trim())}`,
              { body: remark.trim(), noted_at: occurredAt, duration_minutes: durationPayload }
            );
          } else {
            await api.post(`/firms/${firmSubdomain}/notes`, {
              body: remark.trim(),
              noted_at: occurredAt,
              duration_minutes: durationPayload,
            });
          }
          await api.delete(`/call-logs/${editing.entry_id || editing.log_id}`);
          toast.success("Converted to note");
        } else {
          const callPayload = isInternal
            ? {
                target_type: "internal",
                called_at: occurredAt,
                remark: remark.trim(),
                duration_minutes: durationPayload,
              }
            : isTrial
              ? {
                  target_type: "trial",
                  trial_firm_name: trialName.trim(),
                  trial_firm_url: trialUrl.trim(),
                  called_at: occurredAt,
                  outcome,
                  call_category: null,
                  remark: remark.trim(),
                  duration_minutes: durationPayload,
                }
              : {
                  target_type: "firm",
                  firm_subdomain: firmSubdomain,
                  called_at: occurredAt,
                  outcome,
                  call_category: category,
                  remark: remark.trim(),
                  duration_minutes: durationPayload,
                };
          await api.post("/call-logs", callPayload);
          await api.delete(`/notes/${editing.entry_id || editing.note_id}`);
          toast.success("Converted to call");
        }
      } else if (isNote) {
        const notePayload = isGeneral
          ? {
              target_type: "general",
              firm_subdomain: null,
              body: remark.trim(),
              noted_at: occurredAt,
              duration_minutes: durationPayload,
            }
          : isTrial
            ? {
                target_type: "trial",
                firm_subdomain: null,
                body: remark.trim(),
                noted_at: occurredAt,
                duration_minutes: durationPayload,
                trial_firm_name: trialName.trim(),
                trial_firm_url: trialUrl.trim(),
              }
            : {
                target_type: "firm",
                firm_subdomain: firmSubdomain,
                body: remark.trim(),
                noted_at: occurredAt,
                duration_minutes: durationPayload,
              };
        if (editing) {
          await api.patch(`/notes/${editing.entry_id || editing.note_id}`, notePayload);
          toast.success("Note updated");
        } else if (isGeneral) {
          await api.post(`/notes/general`, notePayload);
          toast.success("General note logged");
        } else if (isTrial) {
          await api.post(
            `/notes/trial?trial_firm_name=${encodeURIComponent(trialName.trim())}&trial_firm_url=${encodeURIComponent(trialUrl.trim())}`,
            { body: remark.trim(), noted_at: occurredAt, duration_minutes: durationPayload }
          );
          toast.success("Trial firm note logged");
        } else {
          await api.post(`/firms/${firmSubdomain}/notes`, notePayload);
          toast.success("Note logged");
        }
      } else {
        // Call (firm, internal, or trial)
        const payload = isInternal
          ? {
              target_type: "internal",
              firm_subdomain: null,
              called_at: occurredAt,
              remark: remark.trim(),
              duration_minutes: durationPayload,
              call_category: null,
            }
          : isTrial
            ? {
                target_type: "trial",
                firm_subdomain: null,
                trial_firm_name: trialName.trim(),
                trial_firm_url: trialUrl.trim(),
                called_at: occurredAt,
                outcome,
                call_category: null,
                remark: remark.trim(),
                duration_minutes: durationPayload,
              }
            : {
                target_type: "firm",
                firm_subdomain: firmSubdomain,
                called_at: occurredAt,
                outcome,
                call_category: category,
                remark: remark.trim(),
                duration_minutes: durationPayload,
              };
        if (editing) {
          await api.patch(`/call-logs/${editing.entry_id || editing.log_id}`, payload);
          toast.success("Log updated");
        } else {
          await api.post("/call-logs", payload);
          toast.success(
            isInternal ? "Internal call logged"
              : isTrial ? "Trial firm call logged"
                : "Call logged"
          );
        }
      }
      onSaved({ remark });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSubmitting(false);
    }
  };

  // Lock the firm field only when:
  //  - the caller pins it (Firm 360 / Log Call button), OR
  //  - we're editing an existing FIRM call/note (don't swap firms mid-edit).
  // Editing an internal call or general note + toggling back to Firm leaves
  // the picker unlocked so users can attach a firm.
  const firmLocked =
    !!lockedFirm ||
    (!!editing && target === "firm" && !!editing.firm_subdomain
      && editing.target_type !== "internal" && editing.target_type !== "general");
  const lockedDisplay = lockedFirm
    ? lockedFirm
    : (editing && editing.firm_subdomain
        ? { firm_name: editing.firm_name, subdomain: editing.firm_subdomain }
        : null);

  const titleVerb = editing ? "Edit" : "Log";
  // When the user can freely choose, show the current selection in the title so
  // it stays clear what they're saving as ("Edit note" can still be converted
  // back to a call via the Type chooser).
  const titleNoun = typeLocked || editing
    ? (isNote
        ? (isGeneral ? "general note" : "note")
        : (isInternal ? "internal call" : "call"))
    : "a call/note";
  const dateLabel = isNote ? "Date of note" : "Date of call";
  const timeLabel = isNote ? "Time of note" : "Time of call";
  const remarkLabel = isNote ? "Note" : "Remark";
  const remarkPlaceholder = isNote
    ? (isGeneral
        ? "Free-form team / personal context — observations, follow-ups, ideas…"
        : "Any context the management should know — observations, next steps…")
    : isInternal
      ? "What was the internal call about — context, decisions, follow-ups…"
      : (remarkRequired
          ? "What did they say? Any commitments, blockers, follow-ups…"
          : "Optional — anything worth noting?");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] flex flex-col" data-testid="cl-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            {isNote
              ? <Note size={18} weight="fill" className="text-violet-600" />
              : <PhoneCall size={18} weight="fill" className="text-violet-600" />}
            {titleVerb} {titleNoun}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {isNote
              ? "Save a free-form note about a firm — observations, work done, follow-ups."
              : "Log a call you made — outcome, duration, and a short remark."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2 flex-1 overflow-y-auto pr-1 -mr-2">
          {/* Type chooser — hidden when editing or when caller forces a type */}
          {!typeLocked && (
            <div>
              <Label>Type</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <button
                  type="button"
                  onClick={() => setType("call")}
                  className={`px-3 py-2 rounded-md border text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                    type === "call"
                      ? "bg-violet-50 border-violet-300 text-violet-700 ring-2 ring-violet-100"
                      : "border-gray-200 text-gray-600 hover:border-gray-300"
                  }`}
                  data-testid="cl-dialog-type-call"
                >
                  <PhoneCall size={14} weight="fill" /> Call
                </button>
                <button
                  type="button"
                  onClick={() => setType("note")}
                  className={`px-3 py-2 rounded-md border text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                    type === "note"
                      ? "bg-violet-50 border-violet-300 text-violet-700 ring-2 ring-violet-100"
                      : "border-gray-200 text-gray-600 hover:border-gray-300"
                  }`}
                  data-testid="cl-dialog-type-note"
                >
                  <Note size={14} weight="fill" /> Note
                </button>
              </div>
            </div>
          )}

          {/* Call-with / Note-about chooser — hidden when the caller pins a firm */}
          {!targetLocked && (
            <div>
              <Label>{isNote ? "Note about" : "Call with"}</Label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                <button
                  type="button"
                  onClick={() => setTarget("firm")}
                  className={`px-2 py-2 rounded-md border text-sm font-medium transition-all flex items-center justify-center gap-1.5 ${
                    target === "firm"
                      ? "bg-violet-50 border-violet-300 text-violet-700 ring-2 ring-violet-100"
                      : "border-gray-200 text-gray-600 hover:border-gray-300"
                  }`}
                  data-testid="cl-dialog-target-firm"
                >
                  <Buildings size={14} weight="fill" /> Live firm
                </button>
                <button
                  type="button"
                  onClick={() => setTarget(isNote ? "general" : "internal")}
                  className={`px-2 py-2 rounded-md border text-sm font-medium transition-all flex items-center justify-center gap-1.5 ${
                    (target === "internal" || target === "general")
                      ? "bg-violet-50 border-violet-300 text-violet-700 ring-2 ring-violet-100"
                      : "border-gray-200 text-gray-600 hover:border-gray-300"
                  }`}
                  data-testid={isNote ? "cl-dialog-target-general" : "cl-dialog-target-internal"}
                >
                  {isNote ? <Note size={14} weight="fill" /> : <UsersThree size={14} weight="fill" />}
                  {isNote ? "General" : "Internal"}
                </button>
                <button
                  type="button"
                  onClick={() => setTarget("trial")}
                  className={`px-2 py-2 rounded-md border text-sm font-medium transition-all flex items-center justify-center gap-1.5 ${
                    target === "trial"
                      ? "bg-violet-50 border-violet-300 text-violet-700 ring-2 ring-violet-100"
                      : "border-gray-200 text-gray-600 hover:border-gray-300"
                  }`}
                  data-testid="cl-dialog-target-trial"
                >
                  <Buildings size={14} /> Trial firm
                </button>
              </div>
            </div>
          )}

          {/* Trial firm name + URL — only when target=trial */}
          {isTrial && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Trial firm name <span className="text-red-500">*</span></Label>
                <Input
                  value={trialName}
                  onChange={(e) => setTrialName(e.target.value)}
                  placeholder="e.g. Khanna & Associates"
                  className="mt-1"
                  data-testid="cl-dialog-trial-name"
                />
              </div>
              <div>
                <Label>Trial URL <span className="text-red-500">*</span></Label>
                <Input
                  value={trialUrl}
                  onChange={(e) => setTrialUrl(e.target.value)}
                  placeholder="trial1327"
                  className="mt-1 font-mono text-sm"
                  data-testid="cl-dialog-trial-url"
                />
              </div>
            </div>
          )}

          {/* Firm picker — hidden for internal calls, general notes, and trial entries */}
          {!isInternal && !isGeneral && !isTrial && (
          <div>
            <Label>Live firm</Label>
            {firmLocked ? (
              <div className="mt-1.5 px-3 py-2 rounded-md bg-gray-50 border border-gray-200 text-sm" data-testid="cl-dialog-firm-locked">
                <span className="font-medium text-gray-900">{lockedDisplay?.firm_name}</span>
                <span className="font-mono text-[11px] text-gray-500 ml-2">({lockedDisplay?.subdomain})</span>
              </div>
            ) : (
              <>
                <div className="relative mt-1">
                  <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input
                    placeholder="Search firm by name or URL…"
                    value={firmSearch}
                    onChange={(e) => { setFirmSearch(e.target.value); setFirmSubdomain(""); }}
                    className="pl-8"
                    data-testid="cl-dialog-firm-input"
                  />
                </div>
                {firmSearch && !firmSubdomain && (
                  <div className="mt-1.5 max-h-[180px] overflow-y-auto border border-gray-200 rounded-md bg-white shadow-sm">
                    {filteredFirms.length === 0 && (
                      <div className="px-3 py-2 text-xs text-gray-500">No firms found.</div>
                    )}
                    {filteredFirms.map((f) => (
                      <button
                        key={f.subdomain}
                        type="button"
                        onClick={() => { setFirmSubdomain(f.subdomain); setFirmSearch(f.firm_name); }}
                        className="w-full text-left px-3 py-2 hover:bg-violet-50 border-b border-gray-100 last:border-0 text-sm flex items-center justify-between"
                        data-testid={`cl-dialog-firm-${f.subdomain}`}
                      >
                        <span>
                          <span className="font-medium text-gray-900">{f.firm_name}</span>
                          <span className="ml-2 text-[11px] text-gray-500 font-mono">{f.subdomain}</span>
                        </span>
                        <span className="flex items-center gap-1.5">
                          {f.tier && <TierBadge tier={f.tier} />}
                          {f.poc && <PocChip name={f.poc} showName />}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
                {firmSubdomain && (
                  <div className="mt-1.5 text-[11px] text-emerald-700 flex items-center gap-1">
                    ✓ Selected: <span className="font-mono">{firmSubdomain}</span>
                  </div>
                )}
              </>
            )}
          </div>
          )}

          {/* Call category — firm calls only, compulsory. Trial calls don't need it. */}
          {isCall && !isInternal && !isTrial && (
            <div>
              <Label>Call category <span className="text-red-500">*</span></Label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                {CALL_CATEGORIES.map((c) => (
                  <button
                    key={c.value}
                    type="button"
                    onClick={() => setCategory(c.value)}
                    title={c.hint}
                    className={`px-2 py-2 rounded-md border text-sm font-medium transition-all flex flex-col items-center justify-center gap-0.5 ${
                      category === c.value
                        ? "bg-violet-50 border-violet-300 text-violet-700 ring-2 ring-violet-100"
                        : "border-gray-200 text-gray-600 hover:border-gray-300"
                    }`}
                    data-testid={`cl-dialog-category-${c.value}`}
                  >
                    <span>{c.label}</span>
                    <span className="text-[10px] font-normal text-gray-400">{c.hint}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Date + Time */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="cl-date">{dateLabel}</Label>
              <Input id="cl-date" type="date" value={date} max={new Date().toISOString().slice(0, 10)} onChange={(e) => setDate(e.target.value)} data-testid="cl-dialog-date" />
            </div>
            <div>
              <Label htmlFor="cl-time">{timeLabel}</Label>
              <Input id="cl-time" type="time" value={time} onChange={(e) => setTime(e.target.value)} data-testid="cl-dialog-time" />
            </div>
          </div>

          {/* Outcome — firm calls only (internal calls + notes don't have an outcome) */}
          {isCall && !isInternal && (
            <div>
              <Label>Outcome</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <button
                  type="button"
                  onClick={() => setOutcome("picked_up")}
                  className={`px-3 py-2 rounded-md border text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                    outcome === "picked_up"
                      ? "bg-emerald-50 border-emerald-300 text-emerald-700 ring-2 ring-emerald-100"
                      : "border-gray-200 text-gray-600 hover:border-gray-300"
                  }`}
                  data-testid="cl-dialog-outcome-picked-up"
                >
                  <PhoneCall size={14} weight="fill" />
                  Picked up
                </button>
                <button
                  type="button"
                  onClick={() => setOutcome("no_answer")}
                  className={`px-3 py-2 rounded-md border text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                    outcome === "no_answer"
                      ? "bg-amber-50 border-amber-300 text-amber-700 ring-2 ring-amber-100"
                      : "border-gray-200 text-gray-600 hover:border-gray-300"
                  }`}
                  data-testid="cl-dialog-outcome-no-answer"
                >
                  <PhoneSlash size={14} weight="fill" />
                  Didn't pick up
                </button>
              </div>
            </div>
          )}

          {/* Duration — calls + picked_up only */}
          {durationAllowed && (
            <div data-testid="cl-dialog-duration-block">
              <Label>Duration <span className="text-gray-400 text-[10px] font-normal">(optional)</span></Label>
              <div className="flex items-center gap-2 mt-1">
                <div className="flex items-center gap-1.5">
                  <Input type="number" min={0} max={8} step={1} inputMode="numeric" value={hours}
                    onChange={(e) => setHours(e.target.value.replace(/[^0-9]/g, "").slice(0, 1))}
                    placeholder="0" className="w-16 text-center font-mono" data-testid="cl-dialog-hours" />
                  <span className="text-xs text-gray-500 font-medium">h</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Input type="number" min={0} max={59} step={1} inputMode="numeric" value={minutes}
                    onChange={(e) => setMinutes(e.target.value.replace(/[^0-9]/g, "").slice(0, 2))}
                    placeholder="0" className="w-16 text-center font-mono" data-testid="cl-dialog-minutes" />
                  <span className="text-xs text-gray-500 font-medium">m</span>
                </div>
                {durationTotal > 0 && (
                  <span className="text-[11px] text-violet-700 bg-violet-50 border border-violet-200 px-2 py-1 rounded-md font-medium" data-testid="cl-dialog-duration-preview">
                    Total: {hoursNum > 0 ? `${hoursNum}h ` : ""}{minutesNum > 0 ? `${minutesNum}m` : (hoursNum > 0 ? "" : "0m")}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Remark / Note body */}
          <div>
            <Label htmlFor="cl-remark">
              {remarkLabel} {remarkRequired ? <span className="text-red-500">*</span> : <span className="text-gray-400 text-[10px] font-normal">(optional)</span>}
            </Label>
            <textarea
              id="cl-remark"
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              rows={4}
              placeholder={remarkPlaceholder}
              className="w-full text-sm rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-100"
              data-testid="cl-dialog-remark"
            />
            <div className="text-[10px] text-gray-400 mt-1">{remark.length} chars</div>
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={!valid || submitting} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="cl-dialog-save">
            {submitting ? "Saving…" : editing ? "Save changes" : (isNote ? "Save note" : "Save log")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
